// Carries the id of the row a details page was opened for, so the originating
// overview list can re-select (gray out) that row - but only when the user
// actually pressed "back" on the details page, not when they navigate to the
// list some other way (e.g. via the menu).
const STORAGE_PREFIX = 'qvend:pendingSelectedId:';

export function setPendingSelectedId(scope: string, id: number | string | null | undefined): void {
    try {
        if (id === null || id === undefined) {
            sessionStorage.removeItem(STORAGE_PREFIX + scope);
        } else {
            sessionStorage.setItem(STORAGE_PREFIX + scope, JSON.stringify(id));
        }
    } catch (error) {
        console.log(error);
    }
}

// Pure read - does not mutate storage, so it is safe to call from a useState
// initializer even under React.StrictMode, which invokes initializers twice
// in development. (A read-and-clear "consume" here would have its value
// wiped out by the second invocation before it ever reached the component.)
export function peekPendingSelectedId(scope: string): number | null {
    try {
        const raw = sessionStorage.getItem(STORAGE_PREFIX + scope);
        return raw ? JSON.parse(raw) : null;
    } catch (error) {
        console.log(error);
        return null;
    }
}

// Clears the pending id so it is only ever applied once. Call this from an
// effect - after the value has already been picked up via peekPendingSelectedId
// - rather than from a state initializer. Safe to call repeatedly.
export function clearPendingSelectedId(scope: string): void {
    try {
        sessionStorage.removeItem(STORAGE_PREFIX + scope);
    } catch (error) {
        console.log(error);
    }
}

// Snapshot of an overview list's current page (rows plus whatever paging/
// cursor info it needs), captured right before opening a details page from
// it. Used by lists (like transactions' seek/cursor pagination) that can't
// just re-request "page N" - there is no such command, only step
// Begin/Previous/Next/End from a cursor - so the only way back to the exact
// same page is to restore what was already on screen instead of re-fetching.
// Applying it is gated the same way as the selection itself: only read it
// when peekPendingSelectedId for the same scope is set, i.e. the user
// actually pressed "Back" on the details page.
const LIST_STATE_PREFIX = 'qvend:pendingListState:';

export function setPendingListState<T>(scope: string, state: T): void {
    try {
        sessionStorage.setItem(LIST_STATE_PREFIX + scope, JSON.stringify(state));
    } catch (error) {
        console.log(error);
    }
}

// Pure read, same rationale as peekPendingSelectedId above.
export function peekPendingListState<T>(scope: string): T | null {
    try {
        const raw = sessionStorage.getItem(LIST_STATE_PREFIX + scope);
        return raw ? (JSON.parse(raw) as T) : null;
    } catch (error) {
        console.log(error);
        return null;
    }
}

export function clearPendingListState(scope: string): void {
    try {
        sessionStorage.removeItem(LIST_STATE_PREFIX + scope);
    } catch (error) {
        console.log(error);
    }
}
